import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/i18n";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import PromoPopup from "@/components/site/PromoPopup";
import SeoManager from "@/seo";
import Home from "@/pages/Home";
import MarketingBranding from "@/pages/MarketingBranding";
import FiereEventiLanci from "@/pages/FiereEventiLanci";
import CaseStudy from "@/pages/CaseStudy";
import ChiSono from "@/pages/ChiSono";
import Contatti from "@/pages/Contatti";
import Checkup from "@/pages/Checkup";
import SitoEssenziale from "@/pages/SitoEssenziale";
import Servizi from "@/pages/Servizi";
import PackageDetail from "@/pages/PackageDetail";
import Cookie from "@/pages/Cookie";
import Privacy from "@/pages/Privacy";
import Pagamento from "@/pages/Pagamento";
import Login from "@/pages/admin/Login";
import Admin from "@/pages/admin/Admin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = location.hash.split("session_id=")[1]?.split("&")[0];
    axios
      .post(`${API}/auth/session`, {}, { headers: { "X-Session-ID": sessionId }, withCredentials: true })
      .then((res) => navigate("/admin", { state: { user: res.data }, replace: true }))
      .catch(() => navigate("/admin/login", { replace: true }));
  }, []);

  return <div className="min-h-screen bg-paper" data-testid="auth-callback" />;
};

const AppShell = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  const isAdmin = location.pathname.startsWith("/admin");

  return (
    <>
      <ScrollToTop />
      <SeoManager />
      {!isAdmin && <Header />}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/marketing-branding" element={<MarketingBranding />} />
          <Route path="/fiere-eventi-lanci" element={<FiereEventiLanci />} />
          <Route path="/case-study" element={<CaseStudy />} />
          <Route path="/chi-sono" element={<ChiSono />} />
          <Route path="/contatti" element={<Contatti />} />
          <Route path="/check-up" element={<Checkup />} />
          <Route path="/sito-essenziale" element={<SitoEssenziale />} />
          <Route path="/servizi" element={<Servizi />} />
          <Route path="/pacchetti/:slug" element={<PackageDetail />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/cookie" element={<Cookie />} />
          <Route path="/pagamento" element={<Pagamento />} />
          <Route path="/admin/login" element={<Login />} />
          <Route path="/admin" element={<Admin />} />
        </Routes>
      </main>
      {!isAdmin && <Footer />}
      {!isAdmin && <PromoPopup />}
      <Toaster position="bottom-right" richColors />
    </>
  );
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <AppShell />
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
